import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Briefcase, Calendar, Building2 } from 'lucide-react';
import profileData from '../data/profileData.json';

const Projects = () => {
  const { projects, projectOrder } = profileData;
  const [selectedDomain, setSelectedDomain] = useState('All');

  // Sort projects based on projectOrder array
  const sortedProjects = projectOrder.map(id => projects.find(p => p.id === id)).filter(Boolean);

  // Get unique domains
  const domains = ['All', ...new Set(sortedProjects.map(p => p.domain))];

  // Filter projects by domain
  const filteredProjects = selectedDomain === 'All'
    ? sortedProjects
    : sortedProjects.filter(p => p.domain === selectedDomain);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">Projects</h1>
          <div className="h-1 w-20 bg-[#2563eb]"></div>
          <p className="mt-6 text-lg text-gray-600">
            Comprehensive portfolio of enterprise projects across various domains and technologies.
          </p>
        </div>

        {/* Domain Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            {domains.map((domain) => (
              <button
                key={domain}
                onClick={() => setSelectedDomain(domain)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  selectedDomain === domain
                    ? 'bg-[#2563eb] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-[#2563eb] hover:text-[#2563eb]'
                }`}
              >
                {domain}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 gap-6">
          {filteredProjects.map((project) => (
            <Card key={project.id} className="hover:shadow-lg transition-shadow duration-200">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <CardTitle className="text-2xl text-[#1e3a5f] mb-2">
                      {project.name}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mt-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} />
                        <span><strong>Client:</strong> {project.client}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Briefcase size={16} />
                        <span><strong>Company:</strong> {project.company}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} />
                        <span>{project.duration}</span>
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800 self-start" variant="secondary">
                    {project.domain}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">Role: {project.role}</h4>
                    <p className="text-gray-700 leading-relaxed">{project.description}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Key Responsibilities:</h4>
                    <ul className="space-y-2">
                      {project.responsibilities.map((resp, index) => (
                        <li key={index} className="text-gray-600 text-sm flex items-start">
                          <span className="text-[#2563eb] mr-2">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="border-gray-300 text-gray-700"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No projects found for the selected domain.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;